import { Container } from "components/core/container"
import { Link } from "components/core/link"
import { PageTitle } from "components/core/pageTitle"
import { SEO } from "components/core/seo"
import { BASE_URL } from "lib/utils"
import { NextPage } from "next"
import React from "react"

const PrivacyPolicyPage: NextPage = () => (
  <div className="bg-white dark:bg-gray-900">
    <Container>
      <PageTitle title="Privacy Policy" subtitle="Version 1.0, effective as of 31 August 2023" />
      <SEO
        title="Privacy policy"
        description="Frontend Toolkit privacy policy"
        ogImage={{
          title: "Privacy policy",
          url: `${BASE_URL}/privacy-policy`,
        }}
      />
      <div className="mx-auto md:py-10">
        <h2 className="text-2xl font-semibold mb-5 mt-8 tracking-tight">1. Introduction</h2>

        <p className="text-gray-600 dark:text-gray-300 mb-3">
          This privacy policy (hereinafter referred to as the &quot;Policy&quot;) applies to the website{" "}
          <Link to="https://www.fetoolkit.io">www.fetoolkit.io</Link> (hereinafter referred to as the
          &quot;Website&quot;). The Website is operated by Damian Frizi (hereinafter referred to as the
          &quot;Operator&quot;). The Operator is committed to protecting the privacy of its users. This Policy sets out
          how the Operator collects, uses, and shares personal data of its users.
        </p>

        <h2 className="text-2xl font-semibold mb-5 mt-8 tracking-tight">2. Collection of Personal Data</h2>

        <p className="text-gray-600 dark:text-gray-300 mb-3">
          The Operator collects personal data from its users when they:
        </p>

        <ul className="list-disc list-outside ml-5 text-gray-500 dark:text-gray-400">
          <li>Sign up for the Website;</li>
          <li>Contact the Operator;</li>
          <li>Use the Website.</li>
        </ul>

        <p className="text-gray-600 dark:text-gray-300 my-3">
          The personal data that the Operator collects may include:
        </p>

        <ul className="list-disc list-outside ml-5 text-gray-500 dark:text-gray-400">
          <li>Name;</li>
          <li>Email address;</li>
          <li>IP address;</li>
          <li>Cookie information;</li>
          <li>Information entered on personal dashboards;</li>
          <li>Stripe subscription information;</li>
          <li>Social media login information.</li>
        </ul>

        <h2 className="text-2xl font-semibold mb-5 mt-8 tracking-tight">3. Use of Personal Data</h2>

        <p className="text-gray-600 dark:text-gray-300 mb-3">
          The Operator uses personal data for the following purposes:
        </p>

        <ul className="list-disc list-outside ml-5 text-gray-500 dark:text-gray-400">
          <li>To provide the Website and its features;</li>
          <li>To communicate with users;</li>
          <li>To improve the Website;</li>
          <li>To analyze the use of the Website;</li>
          <li>To send marketing communications to users.</li>
        </ul>

        <p className="text-gray-600 dark:text-gray-300 my-3">
          The Operator may also use personal data to comply with legal obligations or to protect its rights or
          interests.
        </p>

        <h2 className="text-2xl font-semibold mb-5 mt-8 tracking-tight">4. Sharing of Personal Data</h2>

        <p className="text-gray-600 dark:text-gray-300 mb-3">
          The Operator may share personal data with third parties, such as:
        </p>

        <ul className="list-disc list-outside ml-5 text-gray-500 dark:text-gray-400">
          <li>Service providers who help the Operator operate the Website;</li>
          <li>Advertisers who display ads on the Website;</li>
          <li>Analytics providers who help the Operator analyze the use of the Website.</li>
          <li>Social media platforms that the Operator uses for social media logins.</li>
        </ul>

        <p className="text-gray-600 dark:text-gray-300 mb-3">
          The Operator will only share personal data with third parties that have agreed to protect the privacy of the
          data.
        </p>

        <h2 className="text-2xl font-semibold mb-5 mt-8 tracking-tight">5. Social Media Logins</h2>
        <p className="text-gray-600 dark:text-gray-300 mb-3">
          The Operator allows users to sign up for the Website using social media logins, such as Google and Facebook.
          When a user signs up using a social media login, the Operator will collect the user&apos;s name, email
          address, and profile picture from their social media profile. The Operator will use this data to create a user
          account for the user on the Website and to provide the user with the services they have requested.
        </p>
        <h2 className="text-2xl font-semibold mb-5 mt-8 tracking-tight">6. Cookies</h2>

        <p className="text-gray-600 dark:text-gray-300 mb-3">
          The Operator uses cookies on the Website. Cookies are small files that are placed on a user&apos;s computer
          when they visit the Website. Cookies can be used to collect information about the user&apos;s browsing
          activity.
        </p>
        <p className="text-gray-600 dark:text-gray-300 mb-3">The Operator uses cookies for the following purposes:</p>
        <ul className="list-disc list-outside ml-5 text-gray-500 dark:text-gray-400">
          <li>To keep track of the user&apos;s preferences;</li>
          <li>To improve the user experience;</li>
          <li>To analyze the use of the Website.</li>
        </ul>
        <p className="text-gray-600 dark:text-gray-300 my-3">
          The user can choose to disable cookies on their computer. However, disabling cookies may prevent the user from
          using certain features of the Website.
        </p>

        <h2 className="text-2xl font-semibold mb-5 mt-8 tracking-tight">7. Data Protection Rights</h2>

        <p className="text-gray-600 dark:text-gray-300 mb-3">Users have the following data protection rights:</p>
        <ul className="list-disc list-outside ml-5 text-gray-500 dark:text-gray-400">
          <li>The right to access their personal data;</li>
          <li>The right to correct their personal data;</li>
          <li>The right to delete their personal data;</li>
          <li>The right to restrict the processing of their personal data;</li>
          <li>The right to object to the processing of their personal data;</li>
          <li>The right to data portability;</li>
          <li>The right to withdraw consent.</li>
        </ul>
        <p className="text-gray-600 dark:text-gray-300 my-3">
          Users can exercise their data protection rights by contacting the Operator.
        </p>

        <h2 className="text-2xl font-semibold mb-5 mt-8 tracking-tight">8. Data Security</h2>
        <p className="text-gray-600 dark:text-gray-300 my-3">
          The Operator takes measures to protect the security of personal data. These measures include:
        </p>
        <ul className="list-disc list-outside ml-5 text-gray-500 dark:text-gray-400">
          <li>Using encryption technology to protect personal data;</li>
          <li>Keeping personal data confidential;</li>
          <li>Accessing personal data only when necessary.</li>
        </ul>

        <h2 className="text-2xl font-semibold mb-5 mt-8 tracking-tight">9. Changes to the Policy</h2>

        <p className="text-gray-600 dark:text-gray-300 mb-3">
          The Operator may update this Policy from time to time. The most recent version of the Policy will always be
          available on the Website.
        </p>

        <h2 className="text-2xl font-semibold mb-5 mt-8 tracking-tight">10. Contact Information</h2>

        <p className="text-gray-600 dark:text-gray-400 mb-3">
          If you have any questions about this Policy, please contact the Operator:
        </p>
        <p className="text-gray-600 dark:text-gray-400 mb-3">
          Damian Frizzi
          <br />
          <a
            href="mailto:info@fetoolkit.io"
            className=" font-medium text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:border-indigo-500"
          >
            info[at]fetoolkit.io
          </a>
        </p>
      </div>
    </Container>
  </div>
)

export default PrivacyPolicyPage
